import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Star, ChevronLeft, ChevronRight, ExternalLink, Quote } from 'lucide-react';

const googleReviews = [
  { name: "Josiane Beauchamp", rating: 5, text_fr: "Un grand merci à toute l'équipe! J'ai été très bien accompagnée du début à la fin. Malgré ma situation de crédit difficile, ils ont réussi à me trouver un excellent financement.", text_en: "A big thank you to the entire team! I was very well supported from start to finish. Despite my difficult credit situation, they managed to find me excellent financing." },
  { name: "Marc-Antoine Gagnon", rating: 5, text_fr: "Après avoir été refusé par ma banque, j'ai contacté Crédit Auto Reset. En moins de 48h, j'avais mon approbation! L'équipe est vraiment à l'écoute.", text_en: "After being refused by my bank, I contacted Credit Auto Reset. In less than 48h, I had my approval! The team really listens." },
  { name: "Sandra Tremblay", rating: 5, text_fr: "J'avais peur de ne pas être approuvée à cause de ma faillite, mais l'équipe m'a rassurée et tout s'est bien passé. Mon crédit remonte déjà!", text_en: "I was afraid of not being approved because of my bankruptcy, but the team reassured me and everything went well. My credit is already improving!" },
  { name: "Philippe Dubois", rating: 5, text_fr: "Processus simple et transparent. Pas de mauvaises surprises. J'ai eu mon VUS en une semaine. Le personnel est courtois et professionnel.", text_en: "Simple and transparent process. No bad surprises. I got my SUV in one week. The staff is courteous and professional." },
  { name: "Nathalie Roy", rating: 5, text_fr: "Je ne pensais pas que c'était possible d'avoir un financement avec mon dossier. Mon auto est super et les paiements sont raisonnables. Merci à Alexandre!", text_en: "I didn't think it was possible to get financing with my file. My car is great and the payments are reasonable. Thanks to Alexandre!" },
  { name: "Youssef Benali", rating: 5, text_fr: "En tant que nouvel immigrant, c'était difficile d'obtenir du financement. Crédit Auto Reset m'a aidé sans jugement. Très professionnel et rapide.", text_en: "As a new immigrant, it was difficult to get financing. Credit Auto Reset helped me without judgment. Very professional and fast." },
];

const GoogleReviewsWidget = () => {
  const { language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const trackRef = useRef(null);

  const visibleCount = typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : 3;
  const maxIndex = googleReviews.length - visibleCount;

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setCurrentIndex(prev => prev >= maxIndex ? 0 : prev + 1);
    }, 4000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, maxIndex]);

  const goTo = (dir) => {
    setIsAutoPlaying(false);
    setCurrentIndex(prev => {
      if (dir === 'next') return prev >= maxIndex ? 0 : prev + 1;
      return prev <= 0 ? maxIndex : prev - 1;
    });
  };

  return (
    <section className="py-14 md:py-20 bg-[#0b130e] overflow-hidden" data-testid="google-reviews-widget">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6">
          <div>
            <p className="text-xs font-bold text-green-400 uppercase tracking-[0.2em] mb-4">
              {language === 'en' ? 'Testimonials' : 'Témoignages'}
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              {language === 'en' ? 'What our clients say' : 'Ce que nos clients disent'}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-white/10 rounded-full px-5 py-2.5 border border-white/10">
              <svg className="w-5 h-5" viewBox="0 0 48 48"><path d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" fill="#FFC107"/><path d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" fill="#FF3D00"/><path d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0124 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" fill="#4CAF50"/><path d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" fill="#1976D2"/></svg>
              <span className="text-2xl font-extrabold text-white">4.9</span>
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => goTo('prev')} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors border border-white/10">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={() => goTo('next')} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors border border-white/10">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Review Cards Carousel */}
        <div className="overflow-hidden" ref={trackRef}>
          <div
            className="flex gap-6 transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${currentIndex * (100 / visibleCount)}%)` }}
          >
            {googleReviews.map((review, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-full md:w-[calc(33.333%-16px)]"
              >
                <div className="bg-white/5 border border-white/10 rounded-2xl p-7 md:p-8 h-full flex flex-col hover:bg-white/10 transition-colors duration-300">
                  <Quote className="w-8 h-8 text-[#1a5c3b] mb-4 opacity-60" />
                  <p className="text-gray-300 leading-relaxed text-base flex-1 mb-6">
                    {language === 'en' ? review.text_en : review.text_fr}
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#1a5c3b] to-[#16a085] flex items-center justify-center text-white font-bold text-sm">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-white text-sm">{review.name}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        {[...Array(review.rating)].map((_, j) => (
                          <Star key={j} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        ))}
                        <span className="text-xs text-gray-500 ml-1">Google</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dot indicators + CTA */}
        <div className="flex items-center justify-between mt-8">
          <div className="flex items-center gap-2">
            {googleReviews.slice(0, maxIndex + 1).map((_, i) => (
              <button
                key={i}
                onClick={() => { setCurrentIndex(i); setIsAutoPlaying(false); }}
                className={`rounded-full transition-all duration-300 ${
                  i === currentIndex ? 'w-6 h-2 bg-[#1a5c3b]' : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                }`}
              />
            ))}
          </div>
          <a
            href="https://g.page/r/CXxH1z5HJ2FLEBM/review"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-green-400 hover:text-green-300 font-semibold text-sm transition-colors"
          >
            {language === 'en' ? 'See all reviews on Google' : 'Voir tous les avis sur Google'}
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsWidget;
